import { RoleReducer } from '@leverege/api-redux';
import { GlobalState } from '@leverege/ui-redux';
import { ValueCache, ValuesCache } from '@leverege/value-cache';
import { Config } from '@leverege/plugin';
import Err from '@leverege/error';
import isEqual from 'fast-deep-equal';
import { DelegateActions } from './constants/index.js';
const canCache = new ValuesCache({
  equals: isEqual
});
let Molten;
export default class LocalCanResolver {
  static permsEqual(p1, p2) {
    return p1?.projectId === p2?.projectId && p1?.name === p2?.name && p1?.module === p2?.module;
  }
  static async init({
    molten
  }) {
    Molten = molten;
  }

  // locations/locationId[[update,updateData]]
  static getCacheKey(relationship, actions) {
    let actionPart;
    actionPart = '[';
    for (let i = 0; i < actions.length; i++) {
      if (Array.isArray(actions[i])) {
        actionPart += `[${actions[i].join(',')}]`;
      } else {
        actionPart += actions[i];
      }
      if (i < actions.length - 1) {
        i += ',';
      }
    }
    actionPart += ']';
    const toReturn = `${relationship.objRefUrl}${actionPart}`;
    return toReturn;
  }
  static isDefaultActive() {
    return Config.get('LocalCanResolver', 'isActive', true);
  }
  static isInCompareMode() {
    return Config.get('LocalCanResolver', 'compareModeActive', true);
  }
  constructor({
    relationship,
    usersRoles,
    allRoles,
    molten
  }) {
    this.relationship = relationship;
    this.getUsersRolesCache = new ValueCache();
    this.getRolesWithPermissionsCache = new ValueCache();

    // mostly for testing to avoid global state
    this.usersRoles = usersRoles;
    this.allRoles = allRoles;
    this.molten = molten;
  }
  getUsersRoles() {
    return this.usersRoles || GlobalState.get()?.ui?.allUserRoles;
  }
  getRolesWithPermissions() {
    const stateRoles = this.allRoles || GlobalState.get()?.roles;
    const ur = this.getUsersRoles();
    return this.getRolesWithPermissionsCache.calc((ur, stateRoles) => {
      const projectIds = new Set();
      Object.values(ur).forEach(resourceRoles => {
        resourceRoles?.roles.forEach(role => {
          projectIds.add(role?.projectId);
        });
      });
      let roles = {};
      projectIds.forEach(prjId => {
        roles = {
          ...roles,
          ...RoleReducer.getRolesRef(stateRoles, prjId)
        };
      });
      return roles;
    }, ur, stateRoles);
  }
  getMolten() {
    return this.molten || Molten;
  }
  isActive(delegateAction, relationship, defaultActive) {
    const localCanSettings = relationship.localCanSettings;
    return localCanSettings?.[delegateAction]?.active ?? defaultActive;
  }
  buildResourceChain(relationship) {
    const molten = this.getMolten();
    const api = molten.get('api');
    const prjId = api.options.projectId;
    const sysId = api.options.systemId;
    const resources = [prjId, sysId];
    let rel = relationship;
    do {
      if (rel?.id) {
        resources.push(rel.id);
      }
      rel = rel?.parent;
    } while (rel);
    return resources;
  }
  getActions(delegateAction, relationship, query, body) {
    // TODO: validation for updateAnyData????
    if (delegateAction === DelegateActions.UPDATE) {
      const toReturn = [];
      let hasHeader = false,
        hasData = false;
      if (Array.isArray(body)) {
        body.forEach(pv => {
          if (pv?.path === 'data' || pv?.path.startsWith('data/')) {
            hasData = true;
          } else {
            hasHeader = true;
          }
        });
      } else {
        if (body?.data) {
          hasData = true;
        }
        if (body && Object.keys(body).length > 1) {
          hasHeader = true;
        }
      }
      if (!hasData || hasHeader) {
        toReturn.push('update');
      }
      if (hasData) {
        toReturn.push(['updateData', 'updateAnyData']);
      }
      return toReturn;
    }
    if (delegateAction === DelegateActions.DELETE) {
      // TODO: need way to detect root for delete device
      if (body?.deleteDevice === 'force' || relationship?.isRoot) {
        return ['delete'];
      }
      return [['deleteRelationship', 'delete']];
    }
    if (delegateAction === DelegateActions.CREATE) {
      if (body?.id) {
        return [['createRelationship', 'create']];
      }
      if (body?.data) {
        return ['create', ['updateData', 'updateAnyData']];
      }
      return ['create'];
    }
    return [delegateAction];
  }
  getPermissions(relationship, action) {
    const permissions = [];
    const bp = relationship.blueprint?.model;
    const attr = relationship.attribute?.model;
    const actions = Array.isArray(action) ? action : [action];
    actions.forEach(action => {
      permissions.push({
        projectId: 'imagine',
        module: 'Hierarchy',
        name: action
      }, {
        projectId: 'imagine',
        module: 'Device',
        name: action
      });
      // root level bp action
      if (bp && !attr) {
        permissions.push({
          projectId: bp.projectId,
          module: bp.id,
          name: action
        });
      } else if (attr) {
        permissions.push({
          projectId: bp.projectId,
          module: attr.blueprintId,
          name: `${attr.id}.${action}`
        }, {
          projectId: bp.projectId,
          module: bp.id,
          name: action
        });
      }
    });
    return permissions;
  }
  genericCan(delegateAction, query, body, canOpts) {
    const defaultActive = LocalCanResolver.isDefaultActive();
    if (!this.isActive(delegateAction, this.relationship, defaultActive)) {
      return null;
    }
    let actions;
    if (this.relationship.interfaceUrlMutators?.[delegateAction]) {
      actions = [delegateAction];
    } else {
      actions = this.getActions(delegateAction, this.relationship, query, body);
    }
    const key = LocalCanResolver.getCacheKey(this.relationship, actions);
    return canCache.calc(key, this.calculate, {
      delegateAction,
      canOpts,
      relationship: this.relationship,
      usersRoles: this.getUsersRoles(),
      rolesWithPermissions: this.getRolesWithPermissions(),
      actions,
      isInCompareMode: LocalCanResolver.isInCompareMode()
    });
  }
  calculate = ({
    delegateAction,
    canOpts,
    relationship,
    usersRoles,
    rolesWithPermissions,
    actions,
    isInCompareMode
  }) => {
    let res;
    try {
      let permissionsArr;
      if (relationship.interfaceUrlMutators?.[delegateAction]) {
        // only do this when the url is being mutated
        permissionsArr = relationship.localCanSettings?.[delegateAction]?.permissions;
        if (!permissionsArr) {
          return null;
        }
        permissionsArr = [permissionsArr];
      } else {
        permissionsArr = actions.map(action => this.getPermissions(relationship, action, delegateAction));
      }
      const resources = this.buildResourceChain(relationship);
      res = permissionsArr.every(permissions => resources.some(resource => {
        const knownRoles = usersRoles[resource];
        if (!knownRoles) {
          return false;
        }
        if (knownRoles.isAdmin) {
          return true;
        }
        return Object.keys(knownRoles.roleIds).some(knownRoleId => {
          const knownRoleWithPerms = rolesWithPermissions[knownRoleId];
          if (knownRoleWithPerms == null) {
            throw Err({
              message: 'role assigned missing from downloaded roles',
              ignore: true
            });
          }
          return permissions?.some(permToLookFor => {
            return knownRoleWithPerms.permissions?.some(knownPerm => LocalCanResolver.permsEqual(permToLookFor, knownPerm));
          });
        });
      }));
    } catch (err) {
      if (canOpts?.throw && !err.ignore) {
        throw err;
      }
      return null;
    }
    if (isInCompareMode) {
      if (canOpts?.allowed != null && canOpts.allowed !== res) {
        console.warn('locally calculated and server calculated can diff detected', {
          local: res,
          server: canOpts?.allowed
        });
      }
      return null;
    }
    return res;
  };
}